import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { repository } from '@/repositories';
import { getSupabase } from '@/lib/supabase';
import { me as defaultMe } from '@/data/me';
import type { Profile, Role } from '@/types/domain';

/* ============================================================
   Auth context. The shape below is what the whole app consumes,
   so the two implementations (mock vs real Supabase session) are
   fully isolated here.

   `loading` is critical: while the real session is resolving we
   must NOT treat the user as anonymous, or a logged-in user gets
   bounced to /welcome on every refresh.

   There is intentionally NO in-product role switcher — role is the
   authenticated user's granted permission. Local dev seeds a role
   via VITE_DEV_ROLE (mock mode only).
   ============================================================ */

interface AuthState {
  user: Profile | null;
  status: 'authenticated' | 'anonymous';
  role: Role | undefined;
  loading: boolean;
  /** re-sync from the session (call after a successful Telegram verify) */
  signIn: (user?: Profile) => void;
  signOut: () => void;
  updateProfile: (patch: Partial<Profile>) => void;
}

const AuthCtx = createContext<AuthState | null>(null);
const REAL = import.meta.env.VITE_DATA_SOURCE === 'supabase';

export function AuthProvider({ children }: { children: ReactNode }) {
  // Branch on a build-time constant, so the chosen provider is stable for the
  // session (no conditional-hook hazard).
  return REAL ? <RealAuthProvider>{children}</RealAuthProvider> : <MockAuthProvider>{children}</MockAuthProvider>;
}

/* ── Mock: a signed-in dev session, no backend ── */
const devRole = import.meta.env.VITE_DEV_ROLE as Role | undefined;
const seedUser: Profile = devRole ? { ...defaultMe, role: devRole } : defaultMe;

function MockAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Profile | null>(seedUser);
  const signIn = useCallback((u: Profile = seedUser) => setUser(u), []);
  const signOut = useCallback(() => setUser(null), []);
  const updateProfile = useCallback(
    (patch: Partial<Profile>) => setUser((u) => (u ? { ...u, ...patch } : u)),
    [],
  );
  const value = useMemo<AuthState>(
    () => ({
      user,
      role: user?.role,
      status: user ? 'authenticated' : 'anonymous',
      loading: false,
      signIn,
      signOut,
      updateProfile,
    }),
    [user, signIn, signOut, updateProfile],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

/* ── Real: Supabase session → profile ── */
function RealAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const sync = useCallback(async () => {
    const { data } = await getSupabase().auth.getSession();
    if (!data.session) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await repository.getMe());
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let active = true;
    void sync();
    const { data: sub } = getSupabase().auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      if (!session) {
        setUser(null);
        setLoading(false);
      } else {
        void sync();
      }
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [sync]);

  const signIn = useCallback(() => {
    setLoading(true);
    void sync();
  }, [sync]);
  const signOut = useCallback(() => {
    // scope:'local' — sign out ONLY this device. The supabase-js default is
    // 'global', which revokes the session on every device, so signing out on
    // the phone would also log you out on desktop. We want independent
    // multi-device sessions.
    void getSupabase().auth.signOut({ scope: 'local' });
    setUser(null);
  }, []);
  const updateProfile = useCallback(
    (patch: Partial<Profile>) => setUser((u) => (u ? { ...u, ...patch } : u)),
    [],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      role: user?.role,
      status: user ? 'authenticated' : 'anonymous',
      loading,
      signIn,
      signOut,
      updateProfile,
    }),
    [user, loading, signIn, signOut, updateProfile],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
